import './bootstrap';
import '../css/app.css';

import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createI18n } from 'vue-i18n';
import { ZiggyVue } from '../../vendor/tightenco/ziggy';
import enModule from './i18n/locales/en.js';
import ptModule from './i18n/locales/pt.js';
import { normalizeLocale } from './lib/locale';

function unwrapMessages(moduleValue) {
    let value = moduleValue;

    while (value && typeof value === 'object' && 'default' in value) {
        value = value.default;
    }

    return value && typeof value === 'object' ? value : {};
}

const en = unwrapMessages(enModule);
const pt = unwrapMessages(ptModule);
const messages = { en, pt };

function getMessageByPath(source, path) {
    if (!source || typeof source !== 'object' || typeof path !== 'string') {
        return undefined;
    }

    return path.split('.').reduce((value, segment) => {
        if (!value || typeof value !== 'object' || !(segment in value)) {
            return undefined;
        }

        return value[segment];
    }, source);
}

const i18n = createI18n({
    legacy: false,
    locale: normalizeLocale(document.documentElement.lang),
    fallbackLocale: 'en',
    globalInjection: true,
    messages,
});

i18n.global.setLocaleMessage('en', en);
i18n.global.setLocaleMessage('pt', pt);

const fallbackMessages = { en, pt };
const originalT = i18n.global.t.bind(i18n.global);
const originalTm = i18n.global.tm.bind(i18n.global);
const originalTe = i18n.global.te.bind(i18n.global);

function resolveFallbackValue(key, localeValue) {
    const normalizedLocale = normalizeLocale(localeValue || i18n.global.locale.value);
    const primary = getMessageByPath(fallbackMessages[normalizedLocale], key);
    if (primary !== undefined) {
        return primary;
    }

    const secondaryLocale = normalizedLocale === 'pt' ? 'en' : 'pt';
    return getMessageByPath(fallbackMessages[secondaryLocale], key);
}

i18n.global.t = (key, ...args) => {
    const translated = originalT(key, ...args);
    if (typeof key !== 'string' || translated !== key) {
        return translated;
    }

    const fallback = resolveFallbackValue(key, args[0]);
    return typeof fallback === 'string' ? fallback : translated;
};

i18n.global.tm = key => {
    const translated = originalTm(key);
    if (typeof key !== 'string' || translated !== key) {
        return translated;
    }

    return resolveFallbackValue(key, i18n.global.locale.value);
};

i18n.global.te = (key, localeValue) => {
    if (originalTe(key, localeValue)) {
        return true;
    }

    return resolveFallbackValue(key, localeValue) !== undefined;
};

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

createInertiaApp({
    title: () => appName,
    resolve: name =>
        resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        const pageLang = props.initialPage?.props?.lang;
        i18n.global.locale.value = normalizeLocale(pageLang || document.documentElement.lang);
        document.documentElement.lang = i18n.global.locale.value;

        if (!i18n.global.te('nav_public.howItWorks')) {
            i18n.global.mergeLocaleMessage('en', en);
            i18n.global.mergeLocaleMessage('pt', pt);
        }

        return app.use(plugin).use(i18n).use(ZiggyVue).mount(el);
    },
    progress: {
        color: 'rgb(var(--color-primary-600))',
    },
});
